'use client'

import { useState } from 'react'
import { Button, LinkButton } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard'

// ─── Constants ────────────────────────────────────────────────────────────────

const RELEASE_TAG = 'v2026.10.14.1730'
const VERSION = RELEASE_TAG.replace(/^v/, '')
const GITHUB_REPO = 'https://github.com/Live-CGI/TrueLORE-OBS-Plugin'
const DOWNLOAD_BASE = `${GITHUB_REPO}/releases/download/${RELEASE_TAG}`

const DOWNLOAD_URLS: Record<string, string> = {
  macos: `${DOWNLOAD_BASE}/taffylabs_mvp_obs-${VERSION}-macos-universal.pkg`,
  windows: `${DOWNLOAD_BASE}/taffylabs_mvp_obs-${VERSION}-windows-x64-Setup.exe`,
  linux: `${DOWNLOAD_BASE}/taffylabs_mvp_obs-${VERSION}-x86_64-linux-gnu.deb`,
}

const CHECKSUMS: Record<string, string> = {
  macos: '3f17d29ab73be79ccc71b76424abdb560cd9b5daf15d2f0cb346e89c7a2335fe',
  windows: 'c031a1ee372f0b0a393f4ae5d7d1313c125af3ec3c29b26ee5de24089678274c',
  linux: 'ab7a6f844e78a382fe30e15ef6463fccdf56ceb41856138558acfe4a48a9d0f4',
}

interface PlatformInfo {
  id: string
  name: string
  icon: string
  fileType: string
  requirements: string
  installSteps: string[]
  securityNote: string
  checksumCommand: string
}

const PLATFORMS: PlatformInfo[] = [
  {
    id: 'macos',
    name: 'macOS',
    icon: '\u{F8FF}',
    fileType: '.pkg',
    requirements: 'macOS 12+ (Universal — Apple Silicon & Intel)',
    installSteps: [
      'Download the .pkg installer from the releases page.',
      'Double-click the .pkg file to run the installer.',
      'The plugin is installed to ~/Library/Application Support/obs-studio/plugins/.',
      'Restart OBS Studio if it is running.',
    ],
    securityNote:
      'If macOS shows "unidentified developer", right-click the .pkg and select Open, or go to System Settings \u2192 Privacy & Security \u2192 click "Open Anyway".',
    checksumCommand: 'shasum -a 256 <filename>.pkg',
  },
  {
    id: 'windows',
    name: 'Windows',
    icon: '\u{1FA9F}',
    fileType: '.exe',
    requirements: 'Windows 10+ (x64)',
    installSteps: [
      'Download the .exe installer from the releases page.',
      'Run the installer \u2014 it will detect your OBS installation automatically.',
      'Follow the setup wizard to complete the installation.',
      'Restart OBS Studio if it is running.',
    ],
    securityNote:
      'If Windows SmartScreen blocks the installer, click "More info" then "Run anyway".',
    checksumCommand: 'certutil -hashfile <filename>.exe SHA256',
  },
  {
    id: 'linux',
    name: 'Linux',
    icon: '\u{1F427}',
    fileType: '.deb',
    requirements: 'Ubuntu 24.04+ (x86_64)',
    installSteps: [
      'Download the .deb package from the releases page.',
      'Install via terminal: sudo dpkg -i <filename>.deb',
      'Restart OBS Studio if it is running.',
    ],
    securityNote: '',
    checksumCommand: 'sha256sum <filename>.deb',
  },
]

const HOW_IT_WORKS_STEPS = [
  {
    step: '01',
    title: 'Install',
    description: 'Download and install the plugin for your platform. After successful installation, open OBS open Tools then open TrueLORE settings.',
  },
  {
    step: '02',
    title: 'Authenticate',
    description:
      'Press Login in the plugin settings \u2014 a browser window opens where you sign in to TrueLORE with Google or your email and password, then approve OBS. The plugin signs you in automatically.',
  },
  {
    step: '03',
    title: 'Stream',
    description:
      'Start streaming in OBS as normal to any platform. The plugin mirrors your stream to TrueLORE automatically \u2014 no manual server/key setup needed. Zero re-encoding, minimal CPU overhead.',
  },
]

// ─── Components ───────────────────────────────────────────────────────────────

function PlatformCard({ platform }: { platform: PlatformInfo }) {
  const [expanded, setExpanded] = useState(platform.id === 'macos' || platform.id === 'windows')
  const [checksumOpen, setChecksumOpen] = useState(false)
  const { copied: copiedCommand, copy: copyChecksum } = useCopyToClipboard()

  function handleCopyChecksum() {
    copyChecksum(CHECKSUMS[platform.id])
  }

  return (
    <article
      data-component="PlatformCard"
      data-platform={platform.id}
      className={`rounded-xl border transition-all duration-200 ${
        expanded
          ? 'border-carolina/40 dark:border-carolina/30 bg-white dark:bg-carolina/[0.06]'
          : 'border-carolina/20 dark:border-carolina/10 bg-paper dark:bg-white/[0.03]'
      }`}
    >
      <div className="p-5">
        {/* ── Header ── */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-3">
            <span className="text-[24px]" aria-hidden="true">
              {platform.icon}
            </span>
            <div>
              <h3 className="font-bold text-[15px] text-ink dark:text-cream">
                {platform.name}
              </h3>
              <p className="font-mono text-[11px] tracking-[1px] text-steel dark:text-cream/50">
                {platform.fileType}
              </p>
            </div>
          </div>
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded font-mono text-[10px] tracking-[1.5px] bg-green-500/10 text-green-700 dark:text-green-400 border border-green-500/20">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
            AVAILABLE
          </span>
        </div>

        {/* ── Requirements ── */}
        <p className="text-[13px] text-steel dark:text-cream/60 mb-4">{platform.requirements}</p>

        {/* ── Actions ── */}
        <div className="flex items-center gap-3">
          <LinkButton
            variant="filled"
            size="sm"
            href={DOWNLOAD_URLS[platform.id]}
            className="inline-flex items-center gap-2"
            aria-label={`Download TrueLORE OBS plugin for ${platform.name}`}
          >
            DOWNLOAD {platform.fileType.toUpperCase()}
          </LinkButton>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setExpanded(!expanded)}
            aria-expanded={expanded}
            aria-label={`${expanded ? 'Hide' : 'Show'} install instructions for ${platform.name}`}
          >
            {expanded ? 'HIDE STEPS' : 'INSTALL STEPS'}
          </Button>
        </div>
      </div>

      {/* ── Expanded install instructions ── */}
      {expanded && (
        <div className="px-5 pb-5 pt-0" data-component="InstallSteps">
          <div className="mt-3 rounded-lg bg-cream/30 dark:bg-navy/30 px-4 py-3 space-y-3">
            {/* ── Steps ── */}
            <div>
              <p className="font-mono text-[11px] tracking-[2px] text-carolina-dark/50 dark:text-carolina/50 mb-2">
                INSTALLATION
              </p>
              <ol className="list-decimal list-inside space-y-1.5">
                {platform.installSteps.map((step, i) => (
                  <li
                    key={i}
                    className="text-[13px] text-ink/80 dark:text-cream/70 leading-relaxed"
                  >
                    {step}
                  </li>
                ))}
              </ol>
            </div>

            {/* ── Security note ── */}
            {platform.securityNote && (
              <div className="rounded-lg bg-yellow-dark/5 dark:bg-yellow/5 border border-yellow-dark/15 dark:border-yellow/15 px-3 py-2">
                <p className="font-mono text-[11px] tracking-[1px] text-yellow-dark dark:text-yellow mb-1 font-bold">
                  SECURITY NOTE
                </p>
                <p className="text-[12px] text-ink/70 dark:text-cream/60 leading-relaxed">
                  {platform.securityNote}
                </p>
                <p className="text-[11px] text-steel dark:text-cream/40 mt-1 italic">
                  Signed and notarized builds are coming soon.
                </p>
              </div>
            )}

            {/* ── Checksum verification (collapsible) ── */}
            <div>
              <button
                onClick={() => setChecksumOpen(!checksumOpen)}
                aria-expanded={checksumOpen}
                className="flex items-center gap-1.5 font-mono text-[11px] tracking-[2px] text-carolina-dark/50 dark:text-carolina/50 hover:text-carolina-dark dark:hover:text-carolina transition-colors"
              >
                <svg
                  className={`w-3 h-3 transition-transform duration-200 ${checksumOpen ? 'rotate-90' : ''}`}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="9 6 15 12 9 18" />
                </svg>
                VERIFY DOWNLOAD (SHA-256)
              </button>
              {checksumOpen && (
                <div className="mt-2">
                  <div className="flex items-center gap-2 mb-2">
                    <code className="flex-1 rounded-lg border border-carolina/[0.12] dark:border-carolina/[0.10] bg-white/60 dark:bg-navy/40 px-3 py-2 font-mono text-[11px] text-ink/70 dark:text-cream/60 overflow-x-auto break-all">
                      {CHECKSUMS[platform.id]}
                    </code>
                    <Button
                      variant="filled"
                      size="sm"
                      onClick={handleCopyChecksum}
                      className="shrink-0"
                      aria-label={`Copy SHA-256 checksum for ${platform.name}`}
                    >
                      {copiedCommand ? 'COPIED!' : 'COPY'}
                    </Button>
                  </div>
                  <p className="text-[11px] text-steel dark:text-cream/40">
                    Verify with: <code className="text-ink/60 dark:text-cream/50">{platform.checksumCommand}</code>
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </article>
  )
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function PluginsClient() {
  return (
    <div
      data-component="PluginsClient"
      className="max-w-3xl mx-auto px-1 sm:px-6 pt-20 sm:pt-28 pb-24"
    >
      {/* ── Header ── */}
      <header className="mb-10" data-component="PluginsHeader">
        <p className="font-mono text-[13px] tracking-[5px] text-carolina-dark/60 dark:text-carolina/60 mb-2">
          OBS PLUGIN
        </p>
        <h1 className="font-bold text-[28px] sm:text-[36px] text-ink dark:text-cream leading-tight mb-3">
          TrueLORE OBS Studio Plugin
        </h1>
        <p className="text-[15px] text-steel dark:text-cream/60 leading-relaxed max-w-xl">
          A native OBS plugin that authenticates with your TrueLORE account, manages broadcasts, and
          mirrors your stream to the TrueLORE platform automatically &mdash; no manual server or stream
          key setup required.
        </p>
      </header>

      {/* ── How It Works ── */}
      <section aria-label="How it works" className="mb-10" data-component="HowItWorks">
        <p className="font-mono text-[11px] tracking-[3px] text-carolina-dark/50 dark:text-carolina/50 mb-4 uppercase">
          How It Works
        </p>
        <div className="grid gap-4 sm:grid-cols-3">
          {HOW_IT_WORKS_STEPS.map((item) => (
            <Card
              key={item.step}
              className="p-4"
            >
              <span className="font-mono text-[11px] tracking-[2px] text-carolina-dark/40 dark:text-carolina/40">
                {item.step}
              </span>
              <h3 className="font-bold text-[15px] text-ink dark:text-cream mt-1 mb-2">
                {item.title}
              </h3>
              <p className="text-[13px] text-steel dark:text-cream/60 leading-relaxed">
                {item.description}
              </p>
            </Card>
          ))}
        </div>
      </section>

      {/* ── Download Cards ── */}
      <section aria-label="Download plugin" className="mb-10" data-component="DownloadSection">
        <p className="font-mono text-[11px] tracking-[3px] text-carolina-dark/50 dark:text-carolina/50 mb-4 uppercase">
          Download
        </p>
        <div className="space-y-4">
          {PLATFORMS.map((platform) => (
            <PlatformCard key={platform.id} platform={platform} />
          ))}
        </div>
      </section>
    </div>
  )
}